/**
 * The interview call in the browser (owner request 2026-11-01): the same call controller as live
 * recitation (@mubin/live-client) over the interview socket. The staff member's side makes the offer.
 *
 * Either side may join first and may leave and come back, so each says «hello» (a signal with only
 * `restart`) when it is ready: the applicant after its microphone and connection are set up, and the staff
 * member when it learns the applicant is in the room. On the applicant's hello the staff side starts a new
 * call; on the staff member's hello the applicant starts afresh and says hello back.
 */
import { useEffect, useRef, useState } from "react";
import {
  LiveCall,
  LiveSocket,
  type CallState,
  type SocketState,
  type WebRTCPlatform,
} from "@mubin/live-client";
import { INTERVIEW_WS_PATH, type IceServersResponse } from "@mubin/contracts";

export interface InterviewCall {
  socket: SocketState;
  /** The other side has the room open. */
  peer: boolean;
  call: CallState | null;
  /** The room closed on the server's side: not open now, or opened in another tab. */
  closed: "closed" | "replaced" | null;
  setMuted(muted: boolean): void;
  leave(): void;
}

const platform: WebRTCPlatform = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  RTCPeerConnection: window.RTCPeerConnection as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getUserMedia: (c) => navigator.mediaDevices.getUserMedia(c as MediaStreamConstraints) as any,
};

export function useInterviewCall(opts: {
  enabled: boolean;
  side: "applicant" | "staff";
  interviewId: string | null;
  auth: () => Record<string, unknown>;
  iceServers: () => Promise<IceServersResponse>;
}): InterviewCall {
  const [socketState, setSocketState] = useState<SocketState>("connecting");
  const [peer, setPeer] = useState(false);
  const [call, setCall] = useState<CallState | null>(null);
  const [closed, setClosed] = useState<InterviewCall["closed"]>(null);
  const live = useRef<{ socket: LiveSocket | null; call: LiveCall | null }>({ socket: null, call: null });
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    const id = opts.interviewId;
    if (!opts.enabled || !id) return;
    const audio = new Audio();
    audio.autoplay = true;
    const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}${INTERVIEW_WS_PATH}`;
    const socket = new LiveSocket({
      url,
      auth: () => optsRef.current.auth(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      WebSocket: window.WebSocket as any,
    });
    live.current.socket = socket;
    const hello = () => socket.send({ type: "signal", sessionId: id, data: { restart: true } });
    const stop = () => {
      live.current.call?.hangup();
      live.current.call = null;
      setCall(null);
    };
    const start = () => {
      stop();
      const c = new LiveCall({
        sessionId: id,
        side: opts.side === "staff" ? "teacher" : "student",
        socket,
        platform,
        getIceServers: () => optsRef.current.iceServers(),
      });
      live.current.call = c;
      c.on("state", (s) => live.current.call === c && setCall(s));
      c.on("remoteStream", (stream) => {
        audio.srcObject = stream as unknown as MediaStream;
        void audio.play().catch(() => undefined);
      });
      return c.start();
    };

    const offs = [
      socket.on("state", (s) => {
        setSocketState(s);
        // The applicant gets ready (microphone, connection) on every (re)connect and says so.
        if (s === "ready" && opts.side === "applicant") void start().then(hello);
      }),
      socket.on("presence", (m) => {
        if (m.sessionId !== id) return;
        setPeer(m.present);
        if (!m.present) stop();
        // The staff member asks the applicant to get ready afresh.
        else if (opts.side === "staff") hello();
      }),
      socket.on("signal", (m) => {
        if (m.sessionId !== id || m.data.description || m.data.candidate !== undefined || !m.data.restart)
          return;
        if (opts.side === "staff") void start();
        else void start().then(hello);
      }),
      socket.on("error", (e) => {
        if (e.code === "closed" || e.code === "replaced") {
          stop();
          setClosed(e.code);
        }
      }),
    ];
    socket.open();
    return () => {
      for (const off of offs) off();
      stop();
      socket.close();
      live.current.socket = null;
      audio.srcObject = null;
    };
  }, [opts.enabled, opts.interviewId, opts.side]);

  return {
    socket: socketState,
    peer,
    call,
    closed,
    setMuted: (muted) => live.current.call?.setMuted(muted),
    leave: () => {
      live.current.call?.hangup();
      live.current.call = null;
      live.current.socket?.close();
      setCall(null);
      setPeer(false);
    },
  };
}
